/**
 * Runtime app config from the backend (`GET /meta/config`). This is the single
 * source the SPA reads to learn which features are enabled and who the caller is,
 * so the frontend never duplicates the backend's `SystemSettings.features` flags.
 */
import { http } from "@/lib/api";
import { hydrateSeverity, type TierPolicy } from "@/lib/severity";
import { hydrateTerms, type Term } from "@/lib/terms";
import { hydrateThresholds, type SensorThresholds } from "@/lib/thresholds";
import { hydrateTime } from "@/lib/time";

export interface MetaUser {
  id: number;
  email: string;
  full_name?: string | null;
  roles: string[];
}

/**
 * Logo paths, served rather than bundled so a rebrand needs no rebuild. Each is
 * relative to the SPA's static root or an absolute URL; empty means the
 * deployment ships no artwork and `<Brand>` renders its neutral glyph instead.
 */
export interface BrandAssets {
  emblem: string;
  emblem_dark: string;
  lockup: string;
  lockup_dark: string;
}

export interface MetaConfig {
  app_name: string;
  app_tagline: string;
  org_name: string;
  brand?: BrandAssets;
  url_root: string;
  /** The application timezone (IANA) and short name — every time renders in it; see `lib/time`. */
  timezone: string;
  timezone_label: string;
  data_source: string;
  /** true when the active plane is the synthetic engine (vs real/historical). */
  is_synthetic: boolean;
  /** short label for the always-visible data-plane banner. */
  data_source_banner: string;
  auth_enabled: boolean;
  /** feature-flag map keyed by feature id (e.g. `{ billing: true }`). */
  features: Record<string, boolean>;
  /** Fleet-operations thresholds — see `lib/thresholds`. */
  thresholds?: SensorThresholds;
  /** Alert-severity vocabulary, worst first — see `lib/severity`. */
  severity_tiers?: string[];
  severity_rank?: Record<string, number>;
  /** Per-tier response, time budget and design share — see `lib/severity`. */
  severity_policy?: Record<string, TierPolicy>;
  /** The term registry — one definition per user-facing term; see `lib/terms`. */
  terms?: Record<string, Term>;
  user: MetaUser | null;
}

export async function fetchConfig(signal?: AbortSignal): Promise<MetaConfig> {
  const { data } = await http.get<MetaConfig>("/meta/config", { signal });
  // Hydrate the threshold singleton here rather than in a component effect: the
  // status registry is a plain module and must read the real policy from the very
  // first render that follows the config response.
  hydrateThresholds(data.thresholds);
  hydrateTime(data);
  hydrateSeverity(data.severity_tiers, data.severity_rank, data.severity_policy);
  hydrateTerms(data.terms);
  return data;
}
